import { supabase, isSupabaseAvailable } from './supabaseClient';
import { getCurrentUser } from './baseService';

// ============================================
// CONSENT SERVICE - GDPR Compliance
// ============================================

/**
 * Types of consent that can be given/revoked
 */
export type ConsentType =
  | 'terms'           // Terms of Service (required)
  | 'privacy'         // Privacy Policy (required)
  | 'ai_analysis'     // Use of AI for test analysis (required for AI features)
  | 'third_party'     // Share data with Stripe/providers (required for payments)
  | 'data_export'     // Export own data (tracked per action)
  | 'marketing';      // Marketing emails (optional)

/**
 * Consent record as stored in database
 */
export interface ConsentRecord {
  id: string;
  user_id: string;
  consent_type: ConsentType;
  given: boolean;
  version: string;
  ip_address: string | null;
  user_agent: string | null;
  accepted_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  revocation_reason: string | null;
  created_at: string;
}

/**
 * Audit log entry
 */
export interface AuditLogEntry {
  id: string;
  user_id: string;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  consent_type: ConsentType | null;
  metadata: Record<string, unknown>;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

/**
 * Current versions of consent documents
 * Update these when consent documents change
 */
export const CONSENT_VERSIONS: Record<ConsentType, string> = {
  terms: 'v1.0',
  privacy: 'v1.0',
  ai_analysis: 'v1.0',
  third_party: 'v1.0',
  data_export: 'v1.0',
  marketing: 'v1.0'
};

/**
 * Expiration in days for each consent type
 * null = never expires
 */
export const CONSENT_EXPIRATION_DAYS: Record<ConsentType, number | null> = {
  terms: null,        // Never expires
  privacy: null,      // Never expires
  ai_analysis: 365,   // Annual renewal
  third_party: 365,   // Annual renewal
  data_export: null,  // Per-action, no expiration
  marketing: 365      // Annual renewal
};

/**
 * Labels for consent types (Spanish)
 */
export const CONSENT_LABELS: Record<ConsentType, string> = {
  terms: 'Términos de Servicio',
  privacy: 'Política de Privacidad',
  ai_analysis: 'Uso de IA para análisis',
  third_party: 'Compartir datos con proveedores',
  data_export: 'Exportación de datos',
  marketing: 'Emails de marketing'
};

/**
 * Which consents are required to use the app
 */
export const REQUIRED_CONSENTS: ConsentType[] = ['terms', 'privacy'];

/**
 * Which consents are required for specific features
 */
export const FEATURE_CONSENTS: Record<string, ConsentType> = {
  'ai_analysis': 'ai_analysis',
  'stripe_payment': 'third_party',
  'data_export': 'data_export'
};

// ============================================
// Core Functions
// ============================================

/**
 * Check if user has valid (non-expired, non-revoked) consent
 */
export async function hasValidConsent(type: ConsentType): Promise<boolean> {
  if (!isSupabaseAvailable()) {
    console.warn('Supabase not available, consent check skipped');
    // Fail closed in production (block access), open in development (allow)
    return import.meta.env.DEV;
  }

  try {
    const user = await getCurrentUser();
    if (!user) return false;

    const { data, error } = await supabase!
      .from('consent_records')
      .select('*')
      .eq('user_id', user.id)
      .eq('consent_type', type)
      .eq('given', true)
      .is('revoked_at', null)
      .order('accepted_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('Error checking consent:', error);
      return false;
    }

    if (!data) return false;

    // Check expiration
    if (data.expires_at && new Date(data.expires_at) < new Date()) {
      return false;
    }

    return true;
  } catch (err) {
    console.error('Failed to check consent:', err);
    return false;
  }
}

/**
 * Get all consents for the current user
 */
export async function getAllConsents(): Promise<Record<ConsentType, boolean>> {
  const types: ConsentType[] = ['terms', 'privacy', 'ai_analysis', 'third_party', 'data_export', 'marketing'];
  const result: Record<ConsentType, boolean> = {
    terms: false,
    privacy: false,
    ai_analysis: false,
    third_party: false,
    data_export: false,
    marketing: false
  };

  if (!isSupabaseAvailable()) {
    console.warn('Supabase not available, returning default consents');
    return result;
  }

  try {
    const user = await getCurrentUser();
    if (!user) return result;

    // Get all active consents in one query
    const { data, error } = await supabase!
      .from('consent_records')
      .select('consent_type, given, expires_at')
      .eq('user_id', user.id)
      .eq('given', true)
      .is('revoked_at', null)
      .order('accepted_at', { ascending: false });

    if (error) {
      console.error('Error fetching consents:', error);
      return result;
    }

    // Process results - get latest consent per type
    const now = new Date();
    const seenTypes = new Set<string>();

    for (const consent of data || []) {
      const type = consent.consent_type as ConsentType;

      // Skip if we already processed this type
      if (seenTypes.has(type)) continue;
      seenTypes.add(type);

      // Check if not expired
      if (consent.expires_at && new Date(consent.expires_at) < now) {
        continue;
      }

      if (types.includes(type)) {
        result[type] = consent.given;
      }
    }

    return result;
  } catch (err) {
    console.error('Failed to fetch consents:', err);
    return result;
  }
}

/**
 * Record a new consent (give or deny)
 */
export async function recordConsent(
  type: ConsentType,
  given: boolean,
  metadata?: { ip?: string; userAgent?: string }
): Promise<boolean> {
  if (!isSupabaseAvailable()) {
    console.warn('Supabase not available, consent not recorded');
    // Fail open in dev only - in production this would block signup
    return import.meta.env.DEV;
  }

  try {
    const user = await getCurrentUser();
    if (!user) {
      console.error('No authenticated user, cannot record consent');
      return false;
    }

    const version = CONSENT_VERSIONS[type];
    const expirationDays = CONSENT_EXPIRATION_DAYS[type];
    const expiresAt = expirationDays
      ? new Date(Date.now() + expirationDays * 24 * 60 * 60 * 1000).toISOString()
      : null;

    const { error } = await supabase!
      .from('consent_records')
      .insert({
        user_id: user.id,
        consent_type: type,
        given,
        version,
        expires_at: expiresAt,
        ip_address: metadata?.ip || null,
        user_agent: metadata?.userAgent || navigator?.userAgent || null
      });

    if (error) {
      console.error('Error recording consent:', error);
      return false;
    }

    // Log to audit trail
    await logAuditEvent(
      given ? 'consent_given' : 'consent_denied',
      type,
      { version, expiresAt }
    );

    return true;
  } catch (err) {
    console.error('Failed to record consent:', err);
    return false;
  }
}

/**
 * Record multiple consents at once (for signup)
 */
export async function recordMultipleConsents(
  consents: Partial<Record<ConsentType, boolean>>,
  metadata?: { ip?: string; userAgent?: string }
): Promise<boolean> {
  if (!isSupabaseAvailable()) {
    console.warn('Supabase not available, consents not recorded');
    // Fail open in dev only
    return import.meta.env.DEV;
  }

  try {
    const user = await getCurrentUser();
    if (!user) {
      console.error('No authenticated user, cannot record consents');
      return false;
    }

    const records = Object.entries(consents).map(([type, given]) => {
      const consentType = type as ConsentType;
      const expirationDays = CONSENT_EXPIRATION_DAYS[consentType];
      const expiresAt = expirationDays
        ? new Date(Date.now() + expirationDays * 24 * 60 * 60 * 1000).toISOString()
        : null;

      return {
        user_id: user.id,
        consent_type: consentType,
        given: given || false,
        version: CONSENT_VERSIONS[consentType],
        expires_at: expiresAt,
        ip_address: metadata?.ip || null,
        user_agent: metadata?.userAgent || navigator?.userAgent || null
      };
    });

    const { error } = await supabase!
      .from('consent_records')
      .insert(records);

    if (error) {
      console.error('Error recording multiple consents:', error);
      return false;
    }

    // Log to audit trail
    await logAuditEvent('consents_recorded_bulk', undefined, {
      types: Object.keys(consents),
      givenCount: Object.values(consents).filter(Boolean).length
    });

    return true;
  } catch (err) {
    console.error('Failed to record multiple consents:', err);
    return false;
  }
}

/**
 * Revoke a consent
 */
export async function revokeConsent(type: ConsentType, reason?: string): Promise<boolean> {
  if (!isSupabaseAvailable()) {
    console.warn('Supabase not available, consent not revoked');
    return false;
  }

  try {
    const user = await getCurrentUser();
    if (!user) {
      console.error('No authenticated user, cannot revoke consent');
      return false;
    }

    // Update all active consents of this type to revoked
    const { error } = await supabase!
      .from('consent_records')
      .update({
        revoked_at: new Date().toISOString(),
        revocation_reason: reason || null
      })
      .eq('user_id', user.id)
      .eq('consent_type', type)
      .eq('given', true)
      .is('revoked_at', null);

    if (error) {
      console.error('Error revoking consent:', error);
      return false;
    }

    // Log to audit trail
    await logAuditEvent('consent_revoked', type, { reason });

    return true;
  } catch (err) {
    console.error('Failed to revoke consent:', err);
    return false;
  }
}

// ============================================
// Audit Logging
// ============================================

/**
 * Log an event to the audit trail
 */
export async function logAuditEvent(
  action: string,
  consentType?: ConsentType,
  metadata?: Record<string, unknown>,
  resource?: { type: string; id: string }
): Promise<void> {
  if (!isSupabaseAvailable()) {
    return;
  }

  try {
    const user = await getCurrentUser();
    if (!user) return;

    await supabase!
      .from('audit_log')
      .insert({
        user_id: user.id,
        action,
        consent_type: consentType || null,
        resource_type: resource?.type || null,
        resource_id: resource?.id || null,
        metadata: metadata || {},
        user_agent: navigator?.userAgent || null
      });
  } catch (err) {
    // Don't throw on audit log failures - log and continue
    console.error('Failed to log audit event:', err);
  }
}

// ============================================
// Utility Functions
// ============================================

/**
 * Check if user has all required consents
 */
export async function hasAllRequiredConsents(): Promise<boolean> {
  const consents = await getAllConsents();
  return REQUIRED_CONSENTS.every(type => consents[type]);
}

/**
 * Get list of missing required consents
 */
export async function getMissingRequiredConsents(): Promise<ConsentType[]> {
  const consents = await getAllConsents();
  return REQUIRED_CONSENTS.filter(type => !consents[type]);
}

/**
 * Check consent before performing an action
 * Returns true if allowed, false if blocked
 */
export async function requireConsentFor(feature: string): Promise<boolean> {
  const consentType = FEATURE_CONSENTS[feature];
  if (!consentType) return true; // No consent required for this feature

  const hasConsent = await hasValidConsent(consentType);

  if (!hasConsent) {
    console.warn(`Missing consent for feature: ${feature} (requires: ${consentType})`);
  }

  return hasConsent;
}

/**
 * Get consent history for the current user
 */
export async function getConsentHistory(): Promise<ConsentRecord[]> {
  if (!isSupabaseAvailable()) {
    return [];
  }

  try {
    const user = await getCurrentUser();
    if (!user) return [];

    const { data, error } = await supabase!
      .from('consent_records')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching consent history:', error);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('Failed to fetch consent history:', err);
    return [];
  }
}

/**
 * Get audit log for the current user
 */
export async function getAuditLog(limit: number = 100): Promise<AuditLogEntry[]> {
  if (!isSupabaseAvailable()) {
    return [];
  }

  try {
    const user = await getCurrentUser();
    if (!user) return [];

    const { data, error } = await supabase!
      .from('audit_log')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching audit log:', error);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('Failed to fetch audit log:', err);
    return [];
  }
}
